import * as Popover from '@radix-ui/react-popover'
import { isNil } from 'lodash-es'
import { useTranslations } from 'next-intl'

import { cn, isSameRange as _isSameRange, useFormatDate } from '@/shared/lib'
import { Icon, Notice } from '@/shared/ui'

import { useContractNotice } from '../lib/contract'

type Props = {
  startDate?: Date
  endDate?: Date
  onSelect: (date: Date) => void
  onClose?: (...args: unknown[]) => void
  showCloseButton?: boolean
  className?: string
  availableWeeks?: {
    minAvailableWeeks: number
    maxAvailableWeeks: number
  }
  isExtend?: boolean
}

const DATE_FORMAT = 'yyyy년 MM월 dd일'

export const WeeklyCalendar = ({
  startDate,
  endDate: endDateProp,
  onSelect,
  onClose,
  showCloseButton = true,
  className,
  availableWeeks,
  isExtend = false,
}: Props) => {
  const t = useTranslations('date_selection')

  const notice = useContractNotice({ isExtend })

  const noStartDate = isNil(startDate)

  return (
    <div className={cn('flex w-100 flex-col', className)}>
      <div className="m-6 flex items-center justify-between">
        <h3 className="typo-headline2 font-semibold text-black">
          {t('duration.title')}
        </h3>
        {showCloseButton && (
          <Popover.Close
            className={cn(
              'cursor-pointer rounded-sm',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
            )}
            aria-label="Close"
            onClick={onClose}
          >
            <Icon
              name="close"
              size={24}
              className="text-black"
              strokeWidth={1.5}
            />
            <span className="sr-only">{t('button.close')}</span>
          </Popover.Close>
        )}
      </div>
      {noStartDate ? (
        <div className="mb-6 flex w-100 grow items-center justify-center">
          <p className="typo-body1 font-semibold text-gray-400">
            {t('duration.move_in_date_empty')}
          </p>
        </div>
      ) : (
        <>
          <PeriodList
            startDate={startDate}
            endDate={endDateProp}
            availableWeeks={availableWeeks}
            onSelect={onSelect}
          />
          <div className="p-6 pt-4">
            <Notice.Title>{t('notice.title')}</Notice.Title>
            <Notice.List>
              {notice.map(item => (
                <Notice.Item
                  key={item}
                  className="break-keep whitespace-normal"
                >
                  {item}
                </Notice.Item>
              ))}
            </Notice.List>
          </div>
        </>
      )}
    </div>
  )
}

type PeriodListProps = {
  startDate: Date
  endDate?: Date
  availableWeeks?: {
    minAvailableWeeks: number
    maxAvailableWeeks: number
  } | null
  onSelect: (date: Date) => void
}

const PeriodList = ({
  startDate,
  endDate: endDateProp,
  availableWeeks,
  onSelect,
}: PeriodListProps) => {
  const t = useTranslations('common')
  const { formatDate } = useFormatDate()

  const calculateEndDate = (week: number) => {
    return new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate() + week * 7 - 1,
    )
  }

  const calculateRangeByWeek = (week: number) => {
    return {
      from: startDate,
      to: calculateEndDate(week),
    }
  }

  return (
    <div className="space-y-4 bg-purple-50 p-6">
      <strong className="typo-body1 inline-block font-semibold text-black">
        {formatDate({
          date: startDate,
          format: DATE_FORMAT,
        })}
        ~
      </strong>
      <ul className="grid grid-cols-3 gap-2">
        {Array.from({ length: 12 }).map((_, index) => {
          const week = index + 1
          const endDate = calculateEndDate(week)
          const range = calculateRangeByWeek(week)

          const isSameRange = _isSameRange(range, {
            from: startDate,
            to: endDateProp,
          })

          const disabled =
            !isNil(availableWeeks) &&
            (week < availableWeeks.minAvailableWeeks ||
              week > availableWeeks.maxAvailableWeeks)

          const handleSelect = () => {
            if (disabled) {
              return
            }

            onSelect(endDate)
          }

          return (
            <li key={index}>
              <button
                tabIndex={disabled ? -1 : 0}
                className={cn(
                  'flex h-[60px] w-full cursor-pointer flex-col items-start justify-center rounded-sm bg-white px-3 py-2 shadow-[0px_0px_3px_0px_rgba(0,0,0,0.12)]',
                  'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
                  {
                    'bg-black text-white *:text-white': isSameRange,
                    'cursor-default bg-gray-50 *:text-gray-400': disabled,
                  },
                )}
                onClick={handleSelect}
              >
                <strong className="typo-body1 font-semibold text-black">
                  {t('week', { week })}
                </strong>
                <span className="typo-label2 font-normal text-gray-500">
                  ~{formatDate({ date: endDate })}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
